import { supabase } from '@/shared/lib/supabase'
import { apiClient } from '@/shared/api/client'

export interface Envelope {
  id: string
  sender_id: string
  title: string
  message: string | null
  status: 'draft' | 'sent' | 'completed' | 'voided'
  original_filename: string
  original_hash: string | null
  document_data: string
  country: string
  signing_method: 'electronic' | 'pkcs12'
  created_at: string
  updated_at: string
}

export interface EnvelopeRecipient {
  id: string
  envelope_id: string
  email: string
  name: string
  status: 'pending' | 'sent' | 'viewed' | 'signed' | 'declined'
  access_token: string
  user_id: string | null
  signed_at: string | null
  created_at: string
}

export interface EnvelopeWithRecipients extends Envelope {
  sgw_envelope_recipients: EnvelopeRecipient[]
}

export async function listEnvelopes(): Promise<EnvelopeWithRecipients[]> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase
    .from('sgw_envelopes')
    .select('*, sgw_envelope_recipients(*)')
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

export async function getEnvelope(id: string): Promise<EnvelopeWithRecipients | null> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase
    .from('sgw_envelopes')
    .select('*, sgw_envelope_recipients(*)')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return data
}

export async function createEnvelope(params: {
  senderId: string
  title: string
  message?: string
  filename: string
  documentData: string
  hash?: string
  country?: string
  signingMethod?: 'electronic' | 'pkcs12'
}): Promise<Envelope> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase
    .from('sgw_envelopes')
    .insert({
      sender_id: params.senderId,
      title: params.title,
      message: params.message || null,
      original_filename: params.filename,
      document_data: params.documentData,
      original_hash: params.hash || null,
      status: 'draft',
      country: params.country || 'AR',
      signing_method: params.signingMethod || 'electronic',
    })
    .select()
    .single()

  if (error) throw error
  return data
}

export async function updateEnvelopeDocument(
  envelopeId: string,
  documentData: string
): Promise<void> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { error } = await supabase
    .from('sgw_envelopes')
    .update({ document_data: documentData, updated_at: new Date().toISOString() })
    .eq('id', envelopeId)
  if (error) throw error
}

export async function updateEnvelopeStatus(
  envelopeId: string,
  status: Envelope['status']
): Promise<void> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { error } = await supabase
    .from('sgw_envelopes')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', envelopeId)
  if (error) throw error
}

export async function addRecipient(params: {
  envelopeId: string
  email: string
  name: string
}): Promise<EnvelopeRecipient> {
  if (!supabase) throw new Error('Supabase no configurado')
  const accessToken = crypto.randomUUID()
  const { data, error } = await supabase
    .from('sgw_envelope_recipients')
    .insert({
      envelope_id: params.envelopeId,
      email: params.email,
      name: params.name,
      access_token: accessToken,
      status: 'sent',
    })
    .select()
    .single()

  if (error) throw error
  return data
}

// Token-based operations (via RPC, bypass RLS)

export interface EnvelopeByToken {
  envelope: {
    id: string
    title: string
    message: string | null
    status: string
    original_filename: string
    document_data: string
    country: string
    signing_method: 'electronic' | 'pkcs12'
    created_at: string
  }
  recipient: {
    id: string
    email: string
    name: string
    status: string
    user_id: string | null
    signed_at: string | null
  }
  sender: {
    email: string
    display_name: string
  }
}

export async function getEnvelopeByToken(token: string): Promise<EnvelopeByToken | null> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase.rpc('sgw_get_envelope_by_token', { p_token: token })
  if (error) throw error
  if (data?.error) return null
  return data
}

export async function signWithToken(params: {
  token: string
  userId: string
  signatureImage: string
  signedDocument: string
}): Promise<{ success: boolean; all_signed: boolean; envelope_id: string }> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase.rpc('sgw_sign_with_token', {
    p_token: params.token,
    p_user_id: params.userId,
    p_signature_image: params.signatureImage,
    p_signed_document: params.signedDocument,
    p_ip: null,
    p_ua: navigator.userAgent,
  })
  if (error) throw error
  if (data?.error) throw new Error(data.error)
  return data
}

export async function signEnvelopePki(params: {
  token: string
  p12File: File
  password: string
  signerName?: string
  reason?: string
  location?: string
}): Promise<{ success: boolean; all_signed: boolean; envelope_id?: string; message: string }> {
  const formData = new FormData()
  formData.append('access_token', params.token)
  formData.append('certificate', params.p12File)
  formData.append('password', params.password)
  if (params.signerName) formData.append('signer_name', params.signerName)
  if (params.reason) formData.append('reason', params.reason)
  if (params.location) formData.append('location', params.location)

  const response = await apiClient.post('/api/sign/envelope-pki', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
  return response.data
}

export async function claimRecipient(token: string, userId: string): Promise<boolean> {
  if (!supabase) throw new Error('Supabase no configurado')
  const { data, error } = await supabase.rpc('sgw_claim_recipient', {
    p_token: token,
    p_user_id: userId,
  })
  if (error) throw error
  return data?.claimed ?? false
}
